/**
 * Seat-assignment / reactivation handlers for User Management.
 *
 * Extracted from useUserHandlers (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import {
  activateUser,
  reactivateUser,
  createAndAssignSeat,
  type AvailableLicenseForSeat,
} from '../../services/admin'
import type { User } from '../../types'

interface SeatDeps {
  selectedUser: User | null
  selectedLicense: string
  userToReactivate: User | null
  setFormLoading: (v: boolean) => void
  setFormError: (msg: string | null) => void
  setShowSeatModal: (v: boolean) => void
  setShowReactivateModal: (v: boolean) => void
  setSelectedUser: (user: User | null) => void
  setSelectedLicense: (id: string) => void
  setUserToReactivate: (user: User | null) => void
  setReactivateLicenses: (lics: AvailableLicenseForSeat[]) => void
  loadData: () => Promise<void>
}

interface SeatHandlers {
  handleAssignSeat: () => Promise<void>
  handleConfirmReactivate: () => Promise<void>
}

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function useUserSeatHandlers(deps: SeatDeps): SeatHandlers {
  const { t } = useTranslation('admin')

  const handleAssignSeat = async (): Promise<void> => {
    if (!deps.selectedUser || !deps.selectedLicense) return
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      await createAndAssignSeat({ licenseId: deps.selectedLicense, userId: deps.selectedUser.id })
      await activateUser(deps.selectedUser.id)
      deps.setShowSeatModal(false)
      deps.setSelectedUser(null)
      deps.setSelectedLicense('')
      void deps.loadData()
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.assignSeatFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  const handleConfirmReactivate = async (): Promise<void> => {
    if (!deps.userToReactivate || !deps.selectedLicense) return
    deps.setFormLoading(true)
    deps.setFormError(null)
    try {
      const result = await reactivateUser(deps.userToReactivate.id, deps.selectedLicense)
      if (result.success) {
        deps.setShowReactivateModal(false)
        deps.setUserToReactivate(null)
        deps.setReactivateLicenses([])
        deps.setSelectedLicense('')
        void deps.loadData()
      } else {
        deps.setFormError(
          result.message && result.message.length > 0
            ? result.message
            : t('users.errors.reactivateFailed')
        )
      }
    } catch (err: unknown) {
      deps.setFormError(errorMessage(err, t('users.errors.reactivateFailed')))
    } finally {
      deps.setFormLoading(false)
    }
  }

  return { handleAssignSeat, handleConfirmReactivate }
}
